/** Cached grove trees, mineral outcrops and battlefield cover. */
import { Graphics, type Texture } from 'pixi.js';
import { mix, shade } from '../data/palette.ts';
import type { TextureBank } from './textures.ts';
import type { NodeStageId } from '../sim/systems/nodes.ts';
import { nodeSize } from './nodeart.ts';
import { drawTree as tree } from './treeart.ts';
import { drawOutcrop as outcrop } from './outcropart.ts';

const SIZE = 128;
export const SCENERY_GROUND = 0.88;
/** Shared by sprites, selection and the legacy node-art readers. */
export const sceneryNodeSize = nodeSize;
export interface SceneryNodeBounds { x0: number; y0: number; x1: number; y1: number }

function face(g: Graphics, points: number[], color: number): void {
  g.poly(points).fill(color);
}

/** Split, low stone shelves. Iron is dark host rock with exposed metallic seams. */
function rock(g: Graphics, x: number, y: number, scale: number, ore: boolean, variant = 0, worked = false): void {
  const p = (points: number[]) => points.map((n, i) => n * scale + (i % 2 === 0 ? x : y));
  const stone = ore ? 0x536674 : 0xaeb8b6;
  const crest = worked ? -24 : -37 - (variant % 3) * 5;
  const ridge = variant % 2 ? -12 : 5;
  g.poly(p([-41, -5, -24, -19, 31, -16, 47, 1, 18, 12, -23, 9])).fill({ color: 0x111c23, alpha: 0.28 });
  // A broad cut face with a skewed, five-sided top; no cone or pyramid peak.
  face(g, p([-36, -9, -28, crest + 9, ridge, crest, 33, crest + 13, 38, -9, 6, 5]), stone);
  face(g, p([-28, crest + 9, ridge, crest, 33, crest + 13, 8, crest + 22, -20, crest + 24]), ore ? 0x899a9d : 0xd8dcce);
  face(g, p([8, crest + 22, 33, crest + 13, 38, -9, 6, 5]), shade(stone, 0.6));
  face(g, p([-36, -9, -20, crest + 24, 8, crest + 22, 6, 5]), worked ? mix(stone, 0xe3e5d9, 0.3) : shade(stone, 1.04));
  face(g, p([-36, -9, 6, 5, -21, 8]), shade(stone, 0.68));
  // Bedding and a broken edge give stone a quarried material at normal zoom.
  g.poly(p([-28, -13, -10, -10, 5, -13, 5, -10, -10, -7, -29, -10])).fill(ore ? 0x324553 : 0x849291);
  g.poly(p([-22, crest + 15, ridge, crest + 5, 19, crest + 12, -1, crest + 12])).fill(ore ? 0xa0afaf : 0xe8e6d8);
  if (ore) {
    // Oxidised edges surround cool steel-grey ore, visibly different from limestone.
    face(g, p([-17, crest + 16, -9, crest + 14, -3, -19, -10, -5, -17, -9, -11, -22]), 0xb7794d);
    face(g, p([-13, crest + 17, -9, crest + 16, -6, -20, -12, -9, -14, -11, -9, -21]), 0xd1dadd);
    face(g, p([11, -26, 24, -24, 24, -17, 15, -10, 9, -14]), 0x263a46);
    face(g, p([11, -26, 24, -24, 18, -18, 9, -19]), 0xc1cfd2);
    face(g, p([9, -19, 18, -18, 15, -10, 9, -14]), 0x7b909c);
    face(g, p([24, -17, 30, -19, 30, -10, 22, -4]), 0x955b3e);
  }
}

/** Bakes only the finite set of kind / variant / depletion combinations. */
export class SceneryBank {
  private readonly cache = new Map<string, Texture>();
  private readonly bounds = new Map<string, SceneryNodeBounds>();
  constructor(private readonly bank: TextureBank) {}

  node(kind: number, tile: number, stage: NodeStageId): Texture {
    const variant = ((tile * 2654435761) >>> 0) % 3;
    return this.texture(`node:${kind}:${variant}:${stage}`, (g) => {
      if (kind === 0) tree(g, 64, SIZE * SCENERY_GROUND, 1, variant, stage);
      else outcrop(g, kind === 2, variant, stage);
    });
  }

  /** Conservative visible extents, in world tiles relative to the node's centre. */
  nodeBounds(kind: number, tile: number, stage: NodeStageId): SceneryNodeBounds {
    const variant = ((tile * 2654435761) >>> 0) % 3;
    this.node(kind, tile, stage);
    const b = this.bounds.get(`node:${kind}:${variant}:${stage}`)!;
    const size = sceneryNodeSize(kind);
    return { x0: (b.x0 / SIZE - .5) * size, y0: (b.y0 / SIZE - SCENERY_GROUND) * size,
      x1: (b.x1 / SIZE - .5) * size, y1: (b.y1 / SIZE - SCENERY_GROUND) * size };
  }

  scatter(kind: number, variant: number): Texture {
    return this.texture(`scatter:${kind}:${variant % 3}`, (g) => {
      if (kind === 1) {
        // A battlefield obstacle is a group of rocks, not one house-sized gem.
        for (let i = 0; i < 5; i++) {
          rock(g, 27 + (i % 3) * 33, 94 + Math.floor(i / 3) * 17, 0.38 + (i % 2) * 0.1, false, i + variant);
        }
      } else if (kind === 2) {
        // Walkable scrub stays low even at the largest tactical cover radius.
        tree(g, 35, 84, 0.28, variant + 1, 'worked');
        tree(g, 83, 88, 0.3, variant + 2, 'worked');
        tree(g, 20, 105, 0.32, variant, 'spent');
        rock(g, 66, 109, 0.34, false, variant);
        tree(g, 106, 110, 0.32, variant + 1, 'worked');
        tree(g, 44, 116, 0.3, variant + 2, 'spent');
      } else {
        // A cover circle contains a grove, rather than scaling three trunks
        // into giant trees. At r5.4 each crown remains about four tiles tall.
        const copse = [
          [43, 70, .36], [81, 73, .35],
          [21, 87, .36], [61, 88, .43], [103, 91, .38],
          [38, 108, .42], [78, 110, .4], [108, 116, .35],
        ] as const;
        for (let i = 0; i < copse.length; i++) {
          const [x, y, scale] = copse[i];
          tree(g, x + (variant - 1) * (i % 2 ? 2 : -2), y,
            scale, variant + i, i === 7 && variant === 1 ? 'worked' : 'full');
        }
      }
    });
  }

  private texture(key: string, draw: (g: Graphics) => void): Texture {
    const cached = this.cache.get(key);
    if (cached) return cached;
    const g = new Graphics();
    draw(g);
    const bounds = g.getLocalBounds();
    this.bounds.set(key, { x0: bounds.minX, y0: bounds.minY, x1: bounds.maxX, y1: bounds.maxY });
    const texture = this.bank.bake(g, SIZE, SIZE);
    g.destroy();
    this.cache.set(key, texture);
    return texture;
  }
}
